import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import Icon from "./AppIcon";
import Button from "./ui/Button";
import { useAuth } from "../context/AuthContext";
import { logoSrc } from "../api/client";
import { cn } from "../utils/cn";

// Navigation model. All hiring features are live as of Phase 4/5.
const NAV_ITEMS = [
  { to: "/business", label: "Dashboard", icon: "LayoutDashboard", ready: true, end: true },
  { to: "/business/jobs", label: "Jobs", icon: "Briefcase", ready: true },
  { to: "/business/question-bank", label: "Question Bank", icon: "Library", ready: true },
  { to: "/business/credits", label: "Credits", icon: "Wallet", ready: true },
  { to: "/business/buy-credits", label: "Buy Credits", icon: "CreditCard", ready: true },
  { to: "/business/settings", label: "Settings", icon: "Settings", ready: true },
];

function initials(name) {
  if (!name) return "CO";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "CO";
}

export default function Layout() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/business/login", { replace: true });
  };

  // Co-brand the console shell with the organization's own logo when one is set.
  const brandLogo = logoSrc(organization?.logoUrl);

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      {/* Brand — prepxai product logo (links home) */}
      <div className="flex items-center px-6 h-16 border-b border-border">
        <Link
          to="/business"
          onClick={() => setMobileOpen(false)}
          className="flex items-center transition-opacity hover:opacity-80"
          aria-label="prepxai — go to dashboard"
        >
          <span className="text-2xl font-semibold tracking-tight text-foreground">
            prepx<span className="text-primary">ai</span>
          </span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV_ITEMS.map((item) =>
          item.ready ? (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              <Icon name={item.icon} size={18} />
              <span>{item.label}</span>
            </NavLink>
          ) : (
            <div
              key={item.to}
              className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground/60 cursor-default select-none"
              title="Coming soon"
            >
              <span className="flex items-center gap-3">
                <Icon name={item.icon} size={18} />
                <span>{item.label}</span>
              </span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Soon
              </span>
            </div>
          )
        )}
      </nav>

      {/* Footer / account */}
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-3 py-2">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-foreground">
            {initials(user?.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">
              {user?.name || "Member"}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {user?.email || ""}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          fullWidth
          iconName="LogOut"
          onClick={handleLogout}
          className="mt-1 justify-start text-muted-foreground hover:text-foreground"
        >
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-56 border-r border-border bg-card md:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-56 bg-card shadow-elevation-3">
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main column */}
      <div className="md:pl-56">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-card/80 px-4 backdrop-blur md:px-8">
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Icon name="Menu" size={20} />
          </button>

          <div className="flex min-w-0 flex-1 items-center gap-2">
            {brandLogo ? (
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted p-0.5">
                <img
                  src={brandLogo}
                  alt={`${organization?.name || "Company"} logo`}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <Icon name="Building2" size={18} className="text-muted-foreground" />
            )}
            <span className="truncate text-sm font-medium text-foreground">
              {organization?.name || "Your Company"}
            </span>
            {organization?.status && organization.status !== "active" && (
              <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium capitalize text-warning">
                {organization.status}
              </span>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
